import React from "react";
import { motion } from "framer-motion";
import { FileCheck, ShieldCheck } from "lucide-react";

import TiltedCard from "../../animations/TiltedCard";
import SpotlightCard from "../../animations/SpotlightCard";
import GlareHover from "../../animations/GlareHover";

const AgreementCard = () => {
  return (
    <motion.section
      initial={{
        opacity: 0,
        y: 50,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
      }}
      viewport={{
        once: true,
        amount: 0.25,
      }}
      transition={{
        duration: 0.7,
      }}
      className="
        relative
        overflow-hidden
        rounded-3xl

        /* THEME-AWARE BORDER */
        border
        theme-border

        /* THEME-AWARE BACKGROUND */
        bg-[var(--card-bg)]

        px-5
        py-8
        sm:px-8
        sm:py-10
        lg:px-12
        lg:py-12

        transition-colors
        duration-300
      "
    >
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -right-16
          -top-16
          h-56
          w-56
          rounded-full
          bg-orange-500/10
          blur-[120px]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -bottom-16
          -left-16
          h-56
          w-56
          rounded-full
          bg-orange-400/5
          blur-[120px]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          left-1/2
          top-1/2
          h-72
          w-72
          -translate-x-1/2
          -translate-y-1/2
          rounded-full
          bg-orange-500/5
          blur-[140px]
        "
      />

      {/* =====================================================
          CONTENT
      ====================================================== */}

      <div
        className="
          relative
          z-10
          grid
          items-center
          gap-8
          lg:grid-cols-[1.25fr_0.75fr]
          lg:gap-10
        "
      >
        {/* =====================================================
            LEFT CONTENT
        ====================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            x: -40,
          }}
          whileInView={{
            opacity: 1,
            x: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.65,
          }}
        >
          {/* Label */}

          <motion.p
            initial={{
              opacity: 0,
              letterSpacing: "0.1em",
            }}
            animate={{
              opacity: 1,
              letterSpacing: "0.3em",
            }}
            transition={{
              duration: 0.5,
            }}
            className="
              text-[11px]
              font-bold
              uppercase
              tracking-[0.3em]
              brand-color
              sm:text-xs
              md:text-sm
            "
          >
            [ Legal Agreement ]
          </motion.p>

          {/* Heading */}

          <motion.h2
            initial={{
              opacity: 0,
              y: 25,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              delay: 0.2,
            }}
            className="
              mt-5
              text-3xl
              font-extrabold
              leading-tight
              theme-text-primary
              sm:text-4xl
              lg:text-5xl
            "
          >
            A Commitment to
            <span className="brand-color">
              {" "}
              Transparency,
            </span>
            <br />
            Trust & Professionalism
          </motion.h2>

          {/* Description 1 */}

          <motion.p
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              delay: 0.25,
            }}
            className="
              mt-5
              text-sm
              leading-7
              theme-text-muted
              sm:text-base
              sm:leading-8
            "
          >
            These Terms & Conditions establish the legal relationship between
            Web_Bocket and every client, visitor, or business using our website
            and professional software development services.
          </motion.p>

          {/* Description 2 */}

          <motion.p
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              delay: 0.3,
            }}
            className="
              mt-4
              text-sm
              leading-7
              theme-text-muted
              sm:text-base
              sm:leading-8
            "
          >
            By accessing our platform, requesting our services, or continuing
            to use our website, you acknowledge that you have read, understood,
            and agreed to comply with these Terms. Our objective is to maintain
            transparency, fairness, and accountability throughout every
            professional engagement.
          </motion.p>
        </motion.div>

        {/* =====================================================
            RIGHT CARD
        ====================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            x: 40,
          }}
          whileInView={{
            opacity: 1,
            x: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.65,
          }}
        >
          <TiltedCard
            containerHeight="100%"
            containerWidth="100%"
          >
            <SpotlightCard
              tailwindColor="var(--brand-primary)"
              alpha={0.12}
              radius={320}
              className="
                h-full
                w-full
                rounded-2xl
              "
            >
              <GlareHover
                borderRadius="16px"
                borderColor="rgba(249,115,22,0.18)"
                glareColor="#E67000"
                glareOpacity={0.12}
                className="
                  h-full
                  w-full
                "
              >
                <motion.div
                  animate={{
                    y: [0, -8, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  whileHover={{
                    y: -6,
                  }}
                  className="
                    group
                    relative
                    overflow-hidden
                    rounded-2xl

                    /* THEME BORDER */
                    border
                    theme-border

                    /* THEME CARD */
                    theme-card

                    p-6
                    sm:p-7
                    lg:p-8

                    transition-colors
                    duration-300
                  "
                >
                  {/* =================================================
                      HOVER GLOW
                  ================================================== */}

                  <div
                    className="
                      pointer-events-none
                      absolute
                      inset-0
                      rounded-2xl
                      bg-[radial-gradient(circle_at_top_right,rgba(249,115,22,0.125),transparent_72%)]
                      opacity-0
                      transition-opacity
                      duration-500
                      group-hover:opacity-100
                    "
                  />

                  {/* =================================================
                      TOP ACCENT
                  ================================================== */}

                  <div
                    className="
                      pointer-events-none
                      absolute
                      inset-x-0
                      top-0
                      h-px
                      bg-gradient-to-r
                      from-transparent
                      via-orange-500/60
                      to-transparent
                      opacity-0
                      transition-opacity
                      duration-500
                      group-hover:opacity-100
                    "
                  />

                  <div className="relative z-10">
                    {/* =================================================
                        ICON
                    ================================================== */}

                    <motion.div
                      whileHover={{
                        rotate: 8,
                        scale: 1.08,
                      }}
                      transition={{
                        duration: 0.3,
                      }}
                      className="
                        mx-auto
                        flex
                        h-16
                        w-16
                        items-center
                        justify-center
                        rounded-2xl

                        /* THEME BORDER */
                        border
                        theme-border

                        /* ORANGE ACCENT BACKGROUND */
                        bg-orange-500/10

                        sm:h-20
                        sm:w-20
                      "
                    >
                      <FileCheck
                        size={36}
                        className="
                          brand-color
                          sm:h-10
                          sm:w-10
                        "
                      />
                    </motion.div>

                    {/* =================================================
                        HEADING
                    ================================================== */}

                    <h3
                      className="
                        mt-6
                        text-center
                        text-xl
                        font-bold
                        theme-text-primary
                        transition-colors
                        duration-300
                        group-hover:text-orange-400
                        sm:text-2xl
                      "
                    >
                      Professional Standards
                    </h3>

                    {/* =================================================
                        DESCRIPTION
                    ================================================== */}

                    <p
                      className="
                        mt-4
                        text-center
                        text-sm
                        leading-7
                        theme-text-muted
                        sm:text-base
                        sm:leading-8
                      "
                    >
                      Every project delivered by Web_Bocket follows ethical
                      business practices, contractual transparency, and
                      internationally recognized software development standards
                      to ensure long-term client satisfaction.
                    </p>

                    {/* =================================================
                        BADGE
                    ================================================== */}

                    <motion.div
                      whileHover={{
                        scale: 1.03,
                      }}
                      className="
                        mt-7
                        flex
                        flex-wrap
                        items-center
                        justify-center
                        gap-2
                        rounded-xl

                        /* THEME-AWARE BORDER */
                        border
                        theme-border

                        /* ORANGE TRANSPARENT BACKGROUND */
                        bg-orange-500/10

                        px-4
                        py-3

                        transition-colors
                        duration-300
                      "
                    >
                      <ShieldCheck
                        size={18}
                        className="brand-color"
                      />

                      <span
                        className="
                          text-sm
                          font-semibold
                          text-orange-400
                          sm:text-base
                        "
                      >
                        Secure • Transparent • Reliable
                      </span>
                    </motion.div>

                    {/* =================================================
                        BOTTOM ACCENT
                    ================================================== */}

                    <motion.div
                      initial={{
                        scaleX: 0,
                      }}
                      whileInView={{
                        scaleX: 1,
                      }}
                      viewport={{
                        once: true,
                      }}
                      transition={{
                        duration: 0.7,
                        delay: 0.35,
                      }}
                      className="
                        mt-7
                        h-[2px]
                        origin-left
                        rounded-full
                        bg-gradient-to-r
                        from-orange-500
                        via-orange-400/50
                        to-transparent
                      "
                    />
                  </div>
                </motion.div>
              </GlareHover>
            </SpotlightCard>
          </TiltedCard>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default AgreementCard;